import React, { useState, useEffect, useCallback } from 'react';
// import { useAuthStore } from '@/app/store/authStore';
import { 
  SortingState, 
} from '@tanstack/react-table';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Loader2, Plus } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { debounce } from 'lodash';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

// Import services
import { 
  listVouchers,
  createVoucher,
  updateVoucher,
  cancelVoucher,
  deleteVoucher,
  listVoucherTypes
} from '@/app/services/voucherService';

// Import types
import { VoucherResponse, VoucherCreate, VoucherUpdate } from '@/app/types';
import { PaginatedResponse, ListQueryParams } from '@/app/types/api';

// Import components
import { columnsVoucher } from './components/columnsVoucher';
import { VoucherDataTable } from './components/VoucherDataTable';
import VoucherForm from './components/VoucherForm';

// Define the sort direction type
type SortDirection = 'asc' | 'desc';

// Extended PaginatedResponse with page count (backend might return different field names)
interface ExtendedPaginatedResponse<T> extends PaginatedResponse<T> {
  page_count?: number;
  pageCount?: number;
  total_pages?: number;
  totalPages?: number;
}

// Filter configuration object for consistent filtering approach
const FILTER_CONFIG = {
  // Filter format to use: 'array' (with field/value/op objects) or 'object' (simple key-value pairs)
  format: 'array',
  
  // Special field configurations
  fields: {
    'party.name': {
      op: 'ilike',
      transform: (value: string): string => `%${value}%` // Wrap with wildcards for contains search
    },
    'voucher_type.name': {
      op: 'ilike',
      transform: (value: string): string => `%${value}%`
    },
    'status': {
      // Map the column id 'status' to the actual field 'is_cancelled'
      field: 'is_cancelled',
      transform: (value: string): boolean => value === 'true'
    }
  }
};

// Define filter object types
interface FilterObject {
  field: string;
  value: string | number | boolean | string[];
  op: string;
}

// Helper function to build filter parameters based on config
const buildFilterParams = (filters: Record<string, string | number | boolean | string[] | { from?: string; to?: string }>, config = FILTER_CONFIG): string => {
  console.log('buildFilterParams called with filters:', filters);
  
  if (config.format === 'array') {
    // Build array of filter objects with field, value, op
    const filterArray: (FilterObject | FilterObject[] | null)[] = Object.entries(filters).map(([field, value]) => {
      // Skip empty values
      if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
        return null;
      }
      
      // Handle array values (multi-select)
      if (Array.isArray(value)) {
        // For voucher_type_id, use 'in' operator
        if (field === 'voucher_type_id') {
          return {
            field: 'voucher_type_id',
            value: value.map(v => Number(v)), // Convert strings to numbers
            op: 'in'
          } as FilterObject;
        }
        // Generic array handling
        return {
          field,
          value,
          op: 'in'
        } as FilterObject;
      }
      
      // Handle date range filters
      if (field === 'transaction_date' && typeof value === 'object' && 'from' in value) {
        const dateFilter = value as { from?: string; to?: string };
        const dateFilters: FilterObject[] = [];
        
        console.log('Processing date filter:', dateFilter);
        
        if (dateFilter.from) {
          // Ensure we add time component for start of day
          const fromDate = `${dateFilter.from} 00:00:00`;
          dateFilters.push({
            field: 'transaction_date',
            value: fromDate,
            op: 'gte' // Greater than or equal
          });
        }
        
        if (dateFilter.to) {
          // Ensure we add time component for end of day
          const toDate = `${dateFilter.to} 23:59:59`;
          dateFilters.push({
            field: 'transaction_date',
            value: toDate,
            op: 'lte' // Less than or equal
          });
        }
        
        console.log('Generated date filters:', dateFilters);
        return dateFilters.length > 0 ? dateFilters : null;
      }
      
      // Get field config
      const fieldConfig = config.fields[field];
      
      // Get actual field name from config or use the original
      const actualField = fieldConfig?.field || field;
      
      // Transform value if needed
      const transformedValue = fieldConfig?.transform ? 
        fieldConfig.transform(String(value)) : 
        String(value);
      
      // Use configured operator or default to 'eq'
      const op = fieldConfig?.op || 'eq';
      
      return {
        field: actualField,
        value: transformedValue,
        op // Use 'op' instead of 'operator'
      } as FilterObject;
    });
    
    // Flatten the array (to handle date range filters that return arrays)
    const flattenedArray = filterArray.flatMap(f => f || []).filter(Boolean);
    
    const result = JSON.stringify(flattenedArray);
    console.log('Final filter JSON:', result);
    return result;
  } else {
    // Simple object format for exact matches
    const filterObj: Record<string, string | number | boolean> = {};
    
    Object.entries(filters).forEach(([field, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        // Apply field mapping if configured
        const actualField = config.fields[field]?.field || field;
        filterObj[actualField] = value;
      }
    });
    
    return JSON.stringify(filterObj);
  }
};

const VouchersPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get URL params with defaults
  const initialPage = parseInt(searchParams.get('page') || '0', 10);
  const initialSearchQuery = searchParams.get('search') || '';
  const initialSortField = searchParams.get('sort_by') || 'transaction_date';
  const initialSortDir = searchParams.get('sort_dir') || 'desc';

  // State for search, pagination, sorting, and dialogs
  const [search, setSearch] = useState<string>(initialSearchQuery);
  const [page, setPage] = useState<number>(initialPage);
  const [limit] = useState<number>(10);
  const [sorting, setSorting] = useState<SortingState>([
    { id: initialSortField, desc: initialSortDir === 'desc' },
  ]);
  const [filters, setFilters] = useState<Record<string, string | number | boolean | string[] | { from?: string; to?: string }>>({});
  const [formOpen, setFormOpen] = useState(false);
  const [editingVoucher, setEditingVoucher] = useState<VoucherResponse | null>(null);
  const [cancellingVoucher, setCancellingVoucher] = useState<VoucherResponse | null>(null);
  const [deletingVoucher, setDeletingVoucher] = useState<VoucherResponse | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Update URL when params change
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (page > 0) params.set('page', page.toString());
    if (search) params.set('search', search);
    
    // Get current sorting
    const currentSort = sorting[0];
    if (currentSort) {
      params.set('sort_by', currentSort.id);
      params.set('sort_dir', currentSort.desc ? 'desc' : 'asc');
    }
    
    // Add filter params to URL
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(`filter_${key}`, String(value));
      }
    });
    
    setSearchParams(params);
  }, [page, search, sorting, filters, setSearchParams]);
  
  // Determine sort parameters for API call
  const getSortParams = useCallback(() => {
    if (sorting.length === 0) return { 
      sort_by: 'transaction_date', 
      sort_dir: 'desc' as SortDirection 
    };
    
    const sort = sorting[0];
    const direction: SortDirection = sort.desc ? 'desc' : 'asc';
    
    return {
      sort_by: sort.id,
      sort_dir: direction,
    };
  }, [sorting]);

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters: Record<string, unknown>): void => {
    console.log('handleFilterChange called with:', newFilters);
    // Create a simplified version of filters that TypeScript can handle
    const typedFilters: Record<string, string | number | boolean | string[] | { from?: string; to?: string }> = {};
    
    Object.entries(newFilters).forEach(([key, value]) => {
      // Skip empty values, but keep date range objects even if partially filled
      if (value === undefined || value === null || value === '') {
        return;
      }
      
      // Handle array values (multi-select)
      if (Array.isArray(value)) {
        // Only add if array has items
        if (value.length > 0) {
          typedFilters[key] = value as string[];
        }
      }
      // Handle date range object
      else if (typeof value === 'object' && 'from' in value) {
        const dateRange = value as { from?: string; to?: string };
        // Only add if at least one date is set
        if (dateRange.from || dateRange.to) {
          typedFilters[key] = dateRange;
        }
      } else if (typeof value === 'string') {
        typedFilters[key] = value;
      } else if (typeof value === 'number') {
        typedFilters[key] = value;
      } else if (typeof value === 'boolean') {
        typedFilters[key] = value;
      } else {
        // Fallback for other types - convert to string
        typedFilters[key] = String(value);
      }
    });
    
    console.log('Processed filters:', typedFilters);
    // Update the filters state
    setFilters(typedFilters);
    setPage(0); // Reset to first page when filters change
  }, []);

  // Event listeners for UI events
  useEffect(() => {
    const handleViewVoucher = (e: CustomEvent<VoucherResponse>): void => {
      setEditingVoucher(e.detail);
      setFormOpen(true);
    };

    const handleEditVoucher = (e: CustomEvent<VoucherResponse>): void => {
      setEditingVoucher(e.detail);
      setFormOpen(true);
    };

    const handleCancelVoucher = (e: CustomEvent<VoucherResponse>): void => {
      setCancellingVoucher(e.detail);
      setCancelDialogOpen(true);
    };

    const handleDeleteVoucher = (e: CustomEvent<VoucherResponse>): void => {
      setDeletingVoucher(e.detail);
      setDeleteDialogOpen(true);
    };

    // Register event listeners
    document.addEventListener('view-voucher', handleViewVoucher as EventListener);
    document.addEventListener('edit-voucher', handleEditVoucher as EventListener);
    document.addEventListener('cancel-voucher', handleCancelVoucher as EventListener);
    document.addEventListener('delete-voucher', handleDeleteVoucher as EventListener);

    // Cleanup
    return () => {
      document.removeEventListener('view-voucher', handleViewVoucher as EventListener);
      document.removeEventListener('edit-voucher', handleEditVoucher as EventListener);
      document.removeEventListener('cancel-voucher', handleCancelVoucher as EventListener);
      document.removeEventListener('delete-voucher', handleDeleteVoucher as EventListener);
    };
  }, []);

  // Fetch vouchers with search, pagination, sorting and filters
  const { data: vouchersData, isLoading: isLoadingVouchers, error: vouchersError } = useQuery<ExtendedPaginatedResponse<VoucherResponse>>({
    queryKey: ['vouchers', page, limit, search, sorting, filters],
    queryFn: () => {
      const { sort_by, sort_dir } = getSortParams();
      const skip = page * limit; // Calculate skip based on page and limit
      
      // Create params object with basic query parameters
      const params: ListQueryParams = {
        skip,
        limit,
        sort_by,
        sort_dir,
      };
      
      // Add search if provided
      if (search) {
        params.search_query = search;
      }
      
      // Handle filters
      if (Object.keys(filters).length > 0) {
        // Build filter parameters based on configuration
        params.filters = buildFilterParams(filters);
        console.log('Filter JSON:', params.filters);
      }
      
      console.log('API Query Params:', params);
      return listVouchers(params);
    },
  });

  // Calculate page count from total items and limit if not directly provided
  const pageCount = vouchersData?.page_count ?? vouchersData?.pageCount ?? 
                   vouchersData?.total_pages ?? vouchersData?.totalPages ?? 
                   (vouchersData ? Math.ceil(vouchersData.total / limit) : 0);

  // Fetch voucher types for the filter dropdown
  const { data: voucherTypesData } = useQuery({
    queryKey: ['voucherTypes'],
    queryFn: () => listVoucherTypes({ limit: 100 }), // Get all voucher types
  });

  // Update columns with voucher type options
  const columnsWithOptions = React.useMemo(() => {
    return columnsVoucher.map(column => {
      if (column.id === 'voucher_type_id' && voucherTypesData?.items) {
        return {
          ...column,
          meta: {
            ...column.meta,
            options: voucherTypesData.items.map(vt => ({
              label: vt.name,
              value: vt.id
            }))
          }
        };
      }
      return column;
    });
  }, [voucherTypesData]);

  // Mutations
  const createVoucherMutation = useMutation({
    mutationFn: (newVoucher: VoucherCreate) => createVoucher(newVoucher),
    onSuccess: () => {
      toast.success('Voucher created successfully');
      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      setFormOpen(false);
    },
    onError: (error) => {
      toast.error('Failed to create voucher');
      console.error('Error creating voucher:', error);
    },
  });

  const updateVoucherMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: VoucherUpdate }) => 
      updateVoucher(id, data),
    onSuccess: () => {
      toast.success('Voucher updated successfully');
      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      setFormOpen(false);
      setEditingVoucher(null);
    },
    onError: (error) => {
      toast.error('Failed to update voucher');
      console.error('Error updating voucher:', error);
    },
  });

  const cancelVoucherMutation = useMutation({
    mutationFn: (id: number) => cancelVoucher(id),
    onSuccess: () => {
      toast.success('Voucher cancelled successfully');
      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      setCancelDialogOpen(false);
      setCancellingVoucher(null);
    },
    onError: (error) => {
      toast.error('Failed to cancel voucher');
      console.error('Error cancelling voucher:', error);
    },
  });

  const deleteVoucherMutation = useMutation({
    mutationFn: (id: number) => deleteVoucher(id),
    onSuccess: () => {
      toast.success('Voucher deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      setDeleteDialogOpen(false);
      setDeletingVoucher(null);
    },
    onError: (error) => {
      toast.error('Failed to delete voucher. Consider cancelling it instead.');
      console.error('Error deleting voucher:', error);
    },
  });

  // Handle form submission
  const handleVoucherSubmit = (data: VoucherCreate | VoucherUpdate): void => {
    if (editingVoucher) {
      updateVoucherMutation.mutate({
        id: editingVoucher.id,
        data: data as VoucherUpdate,
      });
    } else {
      createVoucherMutation.mutate(data as VoucherCreate);
    }
  };

  // Handle search input change
  const handleSearchChange = useCallback((value: string): void => {
    setSearch(value);
    setPage(0); // Reset to first page on search
  }, []);

  // Handle sorting change
  const handleSortingChange = useCallback((newSorting: SortingState): void => {
    setSorting(newSorting);
    setPage(0); // Reset to first page on sort change
  }, []);

  // Handle page change
  const handlePageChange = useCallback((newPage: number): void => {
    setPage(newPage);
  }, []);

  if (vouchersError) {
    return (
      <div className="container mx-auto py-6">
        <div className="bg-destructive/10 p-4 rounded-md flex items-center gap-2 text-destructive">
          <AlertTriangle className="h-5 w-5" />
          <p>Failed to load vouchers. Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Vouchers</h1>
        <Button
          onClick={() => {
            setEditingVoucher(null);
            setFormOpen(true);
          }}
          className="flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Voucher
        </Button>
      </div>

      <div className="bg-white rounded-md shadow">
        <VoucherDataTable 
          data={vouchersData?.items || []}
          columns={columnsWithOptions}
          isLoading={isLoadingVouchers}
          pageCount={pageCount}
          page={page}
          setPage={handlePageChange}
          onSearchChange={handleSearchChange}
          searchValue={search}
          onSortingChange={handleSortingChange}
          sortingState={sorting}
          onFilterChange={handleFilterChange}
        />
      </div>

      {/* Voucher Form Dialog */}
      <VoucherForm
        open={formOpen}
        onOpenChange={setFormOpen}
        onSubmit={handleVoucherSubmit}
        defaultValues={editingVoucher || undefined}
        isLoading={createVoucherMutation.isPending || updateVoucherMutation.isPending}
        mode={editingVoucher ? 'edit' : 'create'}
      />

      {/* Cancel Voucher Confirmation Dialog */}
      <AlertDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Voucher</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel voucher #{cancellingVoucher?.voucher_number}? This will mark the voucher as cancelled and may affect accounting/inventory.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelVoucherMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={cancelVoucherMutation.isPending}
              onClick={() => {
                if (cancellingVoucher) {
                  cancelVoucherMutation.mutate(cancellingVoucher.id);
                }
              }}
              className="bg-orange-600 text-white hover:bg-orange-700"
            >
              {cancelVoucherMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cancelling...
                </>
              ) : (
                'Confirm Cancellation'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Voucher Confirmation Dialog */}
      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Voucher</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete voucher #{deletingVoucher?.voucher_number}? This action cannot be undone. Consider cancelling the voucher instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteVoucherMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteVoucherMutation.isPending}
              onClick={() => {
                if (deletingVoucher) {
                  deleteVoucherMutation.mutate(deletingVoucher.id);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteVoucherMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VouchersPage; 